import { useEffect, useRef, useState } from 'react';

import { suggestAddresses } from '@/api/endpoints/geocode';
import type { GeocodeSuggestion } from '@/types/geocode';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

interface UseGeocodeSuggestValue {
  readonly suggestions: readonly GeocodeSuggestion[];
  readonly loading: boolean;
  readonly error: string | null;
}

export function useGeocodeSuggest(
  query: string,
  limit = 10,
): UseGeocodeSuggestValue {
  const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRequestRef = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const thisRequest = activeRequestRef.current + 1;
    activeRequestRef.current = thisRequest;
    setLoading(true);
    setError(null);

    const timer = window.setTimeout(async () => {
      try {
        const response = await suggestAddresses(trimmed, limit);
        if (activeRequestRef.current !== thisRequest) {
          return;
        }
        setSuggestions([...response.items]);
      } catch (err: unknown) {
        if (activeRequestRef.current !== thisRequest) {
          return;
        }
        setError(err instanceof Error ? err.message : 'unknown error');
        setSuggestions([]);
      } finally {
        if (activeRequestRef.current === thisRequest) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [query, limit]);

  return { suggestions, loading, error };
}
